import { useMemo, useState } from "react";
import useSWR from "swr";
import { ApiFactory } from "../../core/config/ApiFactory";
import { useSnackbar } from "../../core/contexts/SnackbarContext";
import { IClienteProgramaFidelidadePaginado } from "../interface/IClienteProgramaFidelidade";

export const useFetchClientesPorPrograma = () => {
    const api = useMemo(() => ApiFactory.getApi("MS_ESTABELECIMENTOS"), []);
    const snackbar = useSnackbar();
    const [programaUuid, setProgramaUuid] = useState<string | null>(null);
    const [paginaAtual, setPaginaAtual] = useState(1);
    const [nome, setNome] = useState("");
    const [telefone, setTelefone] = useState("");
    const [url, setUrl] = useState<string | null>(null);
    const [loadingExportar, setLoadingExportar] = useState(false);

    const { data: dados, isLoading, mutate } = useSWR<IClienteProgramaFidelidadePaginado>(
        url,
        async (key: string) => {
            const { data } = await api.get(key);
            return data;
        },
        { revalidateOnFocus: true }
    );

    const handleRefresh = () => mutate(undefined, true);

    const handlePaginaChange = (_: React.ChangeEvent<unknown>, pagina: number) => {
        if (!url) return;
        setUrl(url.replace(/(paginaAtual=)\d+/, `paginaAtual=${pagina}`));
        setPaginaAtual(pagina);
    };

    const handleFiltrar = () => {
        if (!programaUuid) return;
        let novaUrl = `/programas-fidelidade/${programaUuid}/clientes?paginaAtual=1`;
        if (nome) novaUrl += `&nome=${encodeURIComponent(nome)}`;
        if (telefone) novaUrl += `&telefone=${encodeURIComponent(telefone)}`;
        setUrl(novaUrl);
        setPaginaAtual(1);
    };

    const handleLimpar = () => {
        setNome("");
        setTelefone("");
        if (programaUuid) {
            setUrl(`/programas-fidelidade/${programaUuid}/clientes?paginaAtual=1`);
        }
        setPaginaAtual(1);
    };

    const handleProgramaChange = (uuid: string | null) => {
        setProgramaUuid(uuid);
        setNome("");
        setTelefone("");
        setPaginaAtual(1);
        setUrl(uuid ? `/programas-fidelidade/${uuid}/clientes?paginaAtual=1` : null);
    };

    const exportar = async () => {
        if (!programaUuid) return;
        setLoadingExportar(true);
        try {
            const params = new URLSearchParams();
            if (nome) params.append("nome", nome);
            if (telefone) params.append("telefone", telefone);
            const query = params.toString();
            const response = await api.get(
                `/programas-fidelidade/${programaUuid}/clientes/exportar${query ? `?${query}` : ""}`,
                { responseType: "blob" }
            );
            const dataHoje = new Date().toISOString().split("T")[0];
            const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = urlBlob;
            link.setAttribute("download", `fidelidade-${dataHoje}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(urlBlob);
        } catch (error) {
            snackbar("Ocorreu um erro ao exportar os clientes do programa", { severity: "error" });
        } finally {
            setLoadingExportar(false);
        }
    };

    return {
        dados,
        isLoading,
        handleRefresh,
        programaUuid,
        paginaAtual,
        nome,
        telefone,
        setNome,
        setTelefone,
        handlePaginaChange,
        handleFiltrar,
        handleLimpar,
        handleProgramaChange,
        exportar,
        loadingExportar,
    };
};
